/**
 * InfoPopover
 *
 * Small "?" icon that, on click, opens a tooltip-style card with help text.
 * Closes on outside-click or Escape. Used to explain UI affordances inline
 * without dragging in a tooltip library — currently used by the audit/error
 * page's "Group by signature" toggle, but it's intentionally generic so
 * other admin surfaces can adopt it.
 */

import React, { useEffect, useRef, useState } from 'react';
import { FiHelpCircle } from 'react-icons/fi';

export default function InfoPopover({ children, label = 'More info', width = 320, side = 'right' }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const popoverStyle = {
    width: `${width}px`,
    ...(side === 'right' ? { left: '24px' } : { right: '24px' }),
  };

  return (
    <span ref={containerRef} className="relative inline-flex items-center">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label={label}
        className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full text-[#7a7060] hover:text-[#145f5f] transition-colors"
      >
        <FiHelpCircle className="text-[14px]" />
      </button>
      {open && (
        <div
          role="dialog"
          className="absolute z-[200] top-full mt-1.5 bg-white rounded-[10px] border shadow-xl p-3 text-[11.5px] leading-relaxed text-[#08111f]"
          style={{ borderColor: '#e8e2d6', boxShadow: '0 12px 32px rgba(8,17,31,.18), 0 0 0 1px rgba(221,214,198,.4)', ...popoverStyle }}
        >
          {children}
        </div>
      )}
    </span>
  );
}
